import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  IconButton,
  TextField,
  Stack,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ModalProps } from './type';
import {
  createClassSection,
  deleteClassSection,
  updateClassSection,
} from 'apis/main';
import { useState, useMemo, useEffect } from 'react';

export default function ClassSectionModals(props: ModalProps) {
  const {
    modalType,
    sectionId,
    open,
    onClose,
    userId,
    collegeId,
    initialData,
  } = props;

  const [formData, setFormData] = useState({
    college_id: collegeId,
    class_section_year: '',
    class_section_semester: '',
    class_section_subject: '',
    class_section_grade: '',
    class_section_name: '',
    class_section_teacher_info: '',
  });

  useEffect(() => {
    if (modalType === 'update' && initialData) {
      setFormData({
        college_id: collegeId,
        class_section_year: String(initialData.class_section_year || ''),
        class_section_semester: String(
          initialData.class_section_semester || ''
        ),
        class_section_subject: String(initialData.class_section_subject || ''),
        class_section_grade: String(initialData.class_section_grade || ''),
        class_section_name: String(initialData.class_section_name || ''),
        class_section_teacher_info: String(
          initialData.class_section_teacher_info || ''
        ),
      });
    }
  }, [modalType, initialData, collegeId]);

  const updateFormData = useMemo(
    () => ({
      class_section_id: sectionId,
      class_section_create_id: userId,
      ...formData,
    }),
    [sectionId, userId, formData]
  );

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newData = { [e.target.name]: e.target.value };
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const handleSubmit = async () => {
    try {
      if (!userId) return;

      if (modalType === 'create') {
        await createClassSection(userId, formData);
      } else if (modalType === 'update' && sectionId) {
        await updateClassSection(userId, updateFormData);
      } else if (modalType === 'delete' && sectionId) {
        await deleteClassSection(userId, sectionId);
      }
      onClose();
    } catch (error) {
      console.error(
        `Бүлгийг ${
          modalType === 'create'
            ? 'үүсгэх'
            : modalType === 'update'
            ? 'засах'
            : 'устгах'
        } амжилтгүй боллоо:`,
        error
      );
    }
  };

  const renderFormFields = (data: typeof formData, isUpdate = false) => (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="class_section_year"
          label="Он"
          value={data.class_section_year}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          name="class_section_semester"
          label="Семестр"
          value={data.class_section_semester}
          onChange={handleChange}
        >
          <MenuItem value="1">1</MenuItem>
          <MenuItem value="2">2</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="class_section_subject"
          placeholder="Шууд оруулах"
          label="Хичээлийн нэр"
          value={data.class_section_subject}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          name="class_section_grade"
          label="Курс"
          value={data.class_section_grade}
          onChange={handleChange}
        >
          {[1, 2, 3, 4].map(grade => (
            <MenuItem key={grade} value={String(grade)}>
              {grade}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="class_section_name"
          label="Бүлэг"
          value={data.class_section_name}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="class_section_teacher_info"
          label="Удирдах багшийн нэр"
          value={data.class_section_teacher_info}
          onChange={handleChange}
        />
      </Grid>
    </Grid>
  );

  const renderContent = () => {
    switch (modalType) {
      case 'update':
        return (
          <>
            <DialogTitle>
              Бүлэг засах
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  {renderFormFields(updateFormData, true)}
                </FormControl>
              </Stack>
            </DialogContent>
          </>
        );

      case 'create':
        return (
          <>
            <DialogTitle>
              Бүлэг үүсгэх
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  {renderFormFields(formData)}
                </FormControl>
              </Stack>
            </DialogContent>
          </>
        );

      case 'delete':
        return (
          <>
            <DialogTitle>
              Үнэхээр устгах уу?
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ mt: 2 }}>
                Устгах товчийг дарвал бүлгийг сэргээх боломжгүй.
              </Typography>
            </DialogContent>
          </>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {renderContent()}
      <DialogActions>
        <Button onClick={onClose}>Цуцлах</Button>
        <Button
          variant="contained"
          color={modalType === 'delete' ? 'error' : 'primary'}
          onClick={handleSubmit}
        >
          {modalType === 'create'
            ? 'Үүсгэх'
            : modalType === 'delete'
            ? 'Устгах'
            : 'Засах'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
